import { Component, OnInit, OnDestroy } from '@angular/core';

import { BehaviorSubject, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { AbstractDialogComponent } from '../../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { DropdownOptionModel } from '../../../../../Common/Forms/models/dropdown-option.model';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { DistributionModel } from '../../../models/distribution.model';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { DistributionService } from '../../../../../Common/TargetingShared/services/distribution.service';
import { DistributionTemplateService } from '../../../../../Common/TargetingShared/services/distribution-template.service';
import { ManageTemplateDialogComponent } from '../../../../../Common/MonitorShared/components/ManageTemplateDialog/manage-template-dialog.component';
import { FullProfileDialogAnimationsEnum } from '../../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { DistributionSummaryModel } from '../../../../../Common/TargetingShared/model/distribution-summary-model';
import { CommonApiErrorEnum } from '@api/genericApi/enums/common-api-error.enum';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { LocalizationService } from '@Core/Localization/services/localization.service';

enum SaveTemplateState {
    NEW,
    UPDATE
}

@Component({
    selector: 'agpr-distribution-save-template-dialog',
    templateUrl: './distribution-save-template-dialog.component.html',
    styleUrls: ['./distribution-save-template-dialog.component.scss'],
    standalone: false
})
export class DistributionSaveTemplateDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    distributionTemplateListItems: DropdownOptionModel<DistributionSummaryModel>[] = [];
    existingDistributionTemplateDropdown: DropdownOptionModel<DistributionSummaryModel> = null;
    newTemplateName: string = '';

    saveType: SaveTemplateState = SaveTemplateState.NEW;
    formValid: boolean = false;
    nameRequired: boolean = false;

    readonly id = 'distributionSaveTemplateDialog';
    readonly saveTemplateState = SaveTemplateState;
    readonly buttonTypes = ButtonTypesEnum;

    private subscriptions: Subscription[] = [];
    private currentDistribution: DistributionModel;

    constructor(
        public coreDialogService: CoreDialogService,
        private distributionService: DistributionService,
        private distributionTemplateService: DistributionTemplateService,
        private messageService: MessageService,
        private localizationService: LocalizationService,
    ) {
        super(coreDialogService, 'DistributionSaveTemplate');
    }

    ngOnInit() {
        this.subscriptions.push(
            this.distributionTemplateService.all$.subscribe(templates => {
                this.distributionTemplateListItems = templates.map(x => new DropdownOptionModel(x.name, x)) || [];
            }),
            this.distributionService.getFocusedResource().subscribe(currentDistribution => {
                this.currentDistribution = currentDistribution.clone();
            })
        );

        this.validate();
    }

    validate() {
        if (this.saveType === SaveTemplateState.UPDATE) {
            this.formValid = !!this.existingDistributionTemplateDropdown?.value;
        } else {
            this.formValid = true;
            this.nameRequired = false;
        }
    }

    create() {
        const newName: string = this.newTemplateName?.trim();

        if (newName.length === 0) {
            this.nameRequired = true;
            this.formValid = false;

            return;
        }

        const template = this.currentDistribution.clone();
        template.id = null;
        template.name = newName;
        template.aiReferenceId = null;

        this.distributionTemplateService.create(template).subscribe(() => this.closeDialog());
    }

    update() {
        // Let the backend determine which reference to use for the template
        this.currentDistribution.aiReferenceId = null;

        this.currentDistribution.name = this.existingDistributionTemplateDropdown.label;
        this.currentDistribution.id = this.existingDistributionTemplateDropdown.value.id;

        this.distributionTemplateService.update(this.currentDistribution).subscribe(() => {
            this.closeDialog();
        });
    }


    openManageTemplatesDialog() {
        const overlayContainer = document.getElementById('main-content');
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );
        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: overlayContainer
            }
        };
        this.coreDialogService.open(ManageTemplateDialogComponent, io);
    }


    cancel() {
        this.closeDialog();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
